import { ConversationType } from "@mirage/service-conversation/router_state_types";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { useAddDashEsvEventListener } from "@mirage/service-fss-native/hooks/nativeApi";
import { tagged } from "@mirage/service-logging";
import { isEsvConfig } from "@mirage/shared/util/esv";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const logger = tagged("esv-navigation");
/**
 * Custom hook that handles ESV navigation message processing
 * Sets up event listeners when in ESV mode and user is logged in
 */
export const useEsvNavigation = (loggedIn: boolean | undefined) => {
  const navigate = useNavigate();
  const { addDashEsvEventListener } = useAddDashEsvEventListener();
  const { isDualModeEnabled, isDualModeLauncher } = useDualMode();

  // Handle navigation messages from launcher window when in ESV mode
  const handleNavigationMessage = useCallback(
    (eventData) => {
      const { payload, actionType } = eventData.messagePayload;
      try {
        // The eventData directly contains the message structure
        if (actionType === "navigate" && payload) {
          const { pathname, search, hash, state } = payload;

          // Skip if already processed (except for RecentChat conversations)
          if (
            state?.processed &&
            state?.conversationType !== ConversationType.RecentChat
          ) {
            logger.debug("Navigation already processed, skipping");
            return;
          }

          // Handle RecentChat conversations specially
          if (state?.conversationType === ConversationType.RecentChat) {
            if (search) {
              logger.debug("RecentChat with search - navigating to home");
              navigate("/", {
                state: { doNotIntercept: true, ...state, processed: false },
              });
            } else {
              logger.debug("RecentChat without search - skipping navigation");
              return;
            }
          }

          const fullPath = `${pathname}${search || ""}${hash || ""}`;

          navigate(fullPath, {
            state: { doNotIntercept: true, ...state },
          });
        } else {
          logger.warn("Invalid message format:", {
            actionType: eventData.actionType,
            hasPayload: !!eventData.payload,
            expectedActionType: "navigate_to_search",
          });
        }
      } catch (error) {
        logger.error("Error in navigation message processing:", error);
      }
    },
    [navigate],
  );

  // Set up message listener for navigation events when in ESV mode and logged in
  useEffect(() => {
    if (
      !isEsvConfig() ||
      !loggedIn ||
      !addDashEsvEventListener ||
      isDualModeLauncher ||
      !isDualModeEnabled
    ) {
      logger.info("Skipping message listener setup - condition not met");
      return;
    }

    const unsubscribe = addDashEsvEventListener(
      "SendMessageToRenderer",
      handleNavigationMessage,
    );

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe?.();
      }
    };
  }, [
    addDashEsvEventListener,
    handleNavigationMessage,
    loggedIn,
    isDualModeLauncher,
    isDualModeEnabled,
  ]);
};
